/** SUNTRONIX ICON PATTERN — jittered-grid scatter (no obvious rows), context icon sets, Sparse/Medium/Dense, responsive
 * (fewer + larger cells on tablet/mobile, not a scaled-down desktop), purely decorative: aria-hidden, no pointer events. */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { SxShapes } from "./SxIcons";

export const PATTERN_SETS = {
  brand: ["laptop", "smartphone", "tablet", "monitor", "server", "box", "truck", "warranty", "redeploy", "qr", "headset", "product"],
  warehouse: ["warehouse", "box", "barcode", "rack", "laptop", "qr", "asset_tag"],
  quotation: ["laptop", "quote", "product", "truck", "monitor", "cart", "smartphone"],
  service: ["repair", "laptop", "headset", "warranty", "service", "smartphone", "help"],
  recovery: ["laptop", "recover", "data_wipe", "redeploy", "recycle", "inspect", "box"],
  custody: ["warehouse", "asset_tag", "rack", "shield", "barcode", "laptop"],
  dispatch: ["box", "truck", "barcode", "laptop", "courier", "qr"],
};
const CELL = { sparse: 230, medium: 175, dense: 125 };
export const OPACITY = { dashboard: 0.06, document: 0.03, label: 0.05, packaging: 0.16 };

function rng(seed) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

function useSize(ref) {
  const [s, setS] = useState({ w: 0, h: 0, vw: 1280 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onR = () => setS((x) => ({ ...x, vw: window.innerWidth }));
    window.addEventListener("resize", onR);
    const ro = new ResizeObserver(([e]) => setS({ w: Math.round(e.contentRect.width), h: Math.round(e.contentRect.height), vw: window.innerWidth }));
    ro.observe(el);
    return () => { ro.disconnect(); window.removeEventListener("resize", onR); };
  }, [ref]);
  return s;
}

export function layoutPattern({ w, h, vw = w, density = "medium", set = "brand", seed = 7 }) {
  const icons = PATTERN_SETS[set] || PATTERN_SETS.brand;
  const bp = vw < 640 ? 1.9 : vw < 1024 ? 1.35 : 1;
  const cell = Math.min(CELL[density] * bp, Math.max(w, h) / ({ sparse: 1.6, medium: 2.6, dense: 3.6 }[density] || 2.6));
  const size = Math.round((vw < 640 ? 46 : 64) * (density === "dense" ? 0.8 : 1));
  const cols = Math.max(1, Math.ceil(w / cell)), rows = Math.max(1, Math.ceil(h / cell));
  const r = rng(seed * 9973 + cols * 31 + rows);
  const x0 = (cols * cell - w) / 2, y0 = -(rows * cell - h) / 2;
  const out = [];
  let prev = -1;
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      if (cols * rows > 2 && r() < (vw < 640 ? 0.35 : 0.14)) continue;
      let k = Math.floor(r() * icons.length);
      if (k === prev) k = (k + 1 + Math.floor(r() * (icons.length - 1))) % icons.length;
      prev = k;
      const jx = r() * Math.max(8, cell - size), jy = r() * Math.max(8, cell - size);
      const px = ((x * cell + (y % 2 ? cell * 0.5 : 0) + jx + x0) % (cols * cell)) - (cols * cell - w) / 2;
      out.push({ name: icons[k], x: px, y: y * cell + jy + y0, rot: Math.round((r() - 0.5) * 44), s: (0.8 + r() * 0.45) * size });
    }
  }
  return out;
}

/** <BrandPattern set="warehouse" density="medium" surface="dashboard" /> — place inside a `relative overflow-hidden` container. */
/** look="silhouette" = Carry Bag Option C language (soft filled translucent-grey devices); look="line" = outline variant. */
export function BrandPattern({ set = "brand", density = "medium", surface = "dashboard", opacity, color = "var(--st-pattern)", seed = 7, className = "", fade = true, look = "silhouette" }) {
  const ref = useRef(null);
  const { w, h, vw } = useSize(ref);
  const uid = useId().replace(/:/g, "");
  const items = useMemo(() => (w && h ? layoutPattern({ w, h, vw, density, set, seed }) : []), [w, h, vw, density, set, seed]);
  const op = opacity ?? OPACITY[surface] ?? 0.05;
  const used = [...new Set(items.map((i) => i.name))];
  return (
    <div ref={ref} aria-hidden="true" className={`pointer-events-none absolute inset-0 select-none ${className}`} data-testid={`brand-pattern-${set}`} data-density={density} data-count={items.length}
      style={fade ? { WebkitMaskImage: "linear-gradient(100deg, transparent 0%, #000 38%, #000 100%)", maskImage: "linear-gradient(100deg, transparent 0%, #000 38%, #000 100%)" } : undefined}>
      {w > 0 && (
        <svg width={w} height={h} focusable="false" style={{ color, opacity: op }} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <defs>{used.map((n) => <symbol key={n} id={`${uid}-${n}`} viewBox="0 0 24 24" overflow="visible">{look === "silhouette"
            ? <><g fill="currentColor" strokeWidth={3}><SxShapes name={n} /></g><g stroke="#fff" strokeWidth={1.05}><SxShapes name={n} /></g></>
            : <SxShapes name={n} />}</symbol>)}</defs>
          {items.map((it, i) => <use key={i} href={`#${uid}-${it.name}`} width={it.s} height={it.s} transform={`translate(${it.x.toFixed(1)} ${it.y.toFixed(1)}) rotate(${it.rot} ${it.s / 2} ${it.s / 2})`} />)}
        </svg>
      )}
    </div>
  );
}
